import {
  getPrivacySettings,
  setAnonymousStatus,
  updateUserProfile,
  getUserProfile,
} from "apis/settingsServices";
import { Check, Pencil, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useUserStore } from "store/userStore";
import {
  getAuth,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { useToastStore } from "../../store/toastStore";
import { useGlobalLoader } from "../../../src/contexts/GlobalLoaderContext";
import DeleteAccountModal from "./components/DeleteAccountModal";

const PrivacySettings: React.FC = () => {
  const { showLoader, hideLoader } = useGlobalLoader();
  const { username } = useUserStore((state) => state);
  const [displayName, setDisplayName] = useState<string>(username || "");
  const [respondAnonymously, setRespondAnonymously] = useState<boolean>(false);
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  const [tempName, setTempName] = useState(username);
  const [privacyData, setPrivacyData] = useState<any>(null);
  // Full profile (name + airlines + homebase). Needed so the name save can call
  // UpdateProfile without wiping the user's airline/homebase selections.
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Password validation states
  const [hasLowercase, setHasLowercase] = useState<boolean>(false);
  const [hasUppercase, setHasUppercase] = useState<boolean>(false);
  const [hasNumber, setHasNumber] = useState<boolean>(false);
  const [hasSpecial, setHasSpecial] = useState<boolean>(false);
  const [hasMinLength, setHasMinLength] = useState<boolean>(false);
  const [passwordsMatch, setPasswordsMatch] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [showCurrentPassword, setShowCurrentPassword] =
    useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const currentPasswordRef = useRef<HTMLInputElement>(null);
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  // iOS/Safari leaves the last character revealed when -webkit-text-security is
  // toggled back on (eye off). Forcing a reflow repaints every glyph as a dot.
  const forceMaskRepaint = (el: HTMLInputElement | null) => {
    if (!el) return;
    const prev = el.style.display;
    el.style.display = "none";
    void el.offsetHeight; // trigger reflow
    el.style.display = prev;
  };
  useEffect(() => {
    forceMaskRepaint(currentPasswordRef.current);
  }, [showCurrentPassword]);
  useEffect(() => {
    forceMaskRepaint(newPasswordRef.current);
  }, [showNewPassword]);
  useEffect(() => {
    forceMaskRepaint(confirmPasswordRef.current);
  }, [showConfirmPassword]);
  const [currentPasswordLengthError, setCurrentPasswordLengthError] = useState<string>("");
  const [newPasswordLengthError, setNewPasswordLengthError] = useState<string>("");
  const [confirmPasswordLengthError, setConfirmPasswordLengthError] = useState<string>("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Strictly prevent input beyond 20 characters
    if (value.length > 20) {
      setError("*Name must be 20 characters or less");
      // Don't update state if it exceeds 20
      return;
    }

    // Validate format
    if (!/^[a-zA-Z\s]*$/.test(value)) {
      setError("*Only letters and spaces are allowed");
    } else {
      setError("");
    }

    setTempName(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    // Prevent typing if already at 20 characters (allow deletion and navigation keys)
    if (target.value.length >= 20 && 
        e.key !== 'Backspace' && 
        e.key !== 'Delete' && 
        e.key !== 'ArrowLeft' && 
        e.key !== 'ArrowRight' &&
        e.key !== 'Tab' &&
        e.key !== 'Home' &&
        e.key !== 'End' &&
        !e.ctrlKey && 
        !e.metaKey &&
        !e.altKey) {
      e.preventDefault();
      setError("*Name must be 20 characters or less");
    }
  };

  const handleEditClick = () => setIsEditing(true);

  const handleCancel = () => {
    setTempName(String(displayName));
    setIsEditing(false);

    // Reset password fields
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError(null);
  };

  const handleNameSave = async () => {
    // Name is the account name (same as My Account / mobile), so persist it via
    // UpdateProfile — NOT UpdatePrivacy. Send the existing airline/homebase IDs
    // alongside the new name so UpdateProfile doesn't clear them.
    if (!profile) {
      useToastStore
        .getState()
        .showToast("Profile is still loading, please try again.", "error", 3000);
      return;
    }
    try {
      await updateUserProfile({
        fullName: String(tempName),
        primaryAirlineId: Number(profile?.primaryAirline?.id) || 0,
        homebaseId: Number(profile?.homebase?.id) || 0,
        additionalAirlineIds: Array.isArray(profile?.additionalAirlines)
          ? profile.additionalAirlines
              .map((a: any) => Number(a?.id))
              .filter((n: number) => !Number.isNaN(n))
          : [],
      });
      setIsEditing(false);
      setDisplayName(String(tempName));
      // Keep the locally-held profile in sync so a second edit still has the
      // airline/homebase IDs.
      setProfile((prev: any) =>
        prev ? { ...prev, fullName: String(tempName) } : prev,
      );
      useUserStore.getState().setUsername(String(tempName));
      useToastStore
        .getState()
        .showToast("Name updated successfully!", "success", 3000);
    } catch (error) {
      console.error("Failed to update name:", error);
      useToastStore
        .getState()
        .showToast("Failed to update name.", "error", 3000);
    }
  };

  const handlePasswordSave = async () => {
    if (!currentPassword) {
      useToastStore
        .getState()
        .showToast("Current password is required.", "error", 3000);
      return;
    }

    if (!isPasswordValid()) {
      useToastStore
        .getState()
        .showToast("Please meet all password requirements.", "error", 3000);
      return;
    }

    try {
      showLoader();
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user || !user.email) {
        useToastStore
          .getState()
          .showToast(
            "User email is not available. Please log in again.",
            "error",
            3000
          );
        return;
      }

      // Re-authenticate user
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);

      // Reset fields after success
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      useToastStore
        .getState()
        .showToast("Password changed successfully!", "success", 3000);
    } catch (error: any) {
      switch (error.code) {
        case "auth/invalid-credential":
          useToastStore
            .getState()
            .showToast(
              "Invalid credentials. Please enter the correct current password.",
              "error",
              3000
            );
          break;
        case "auth/wrong-password":
          useToastStore
            .getState()
            .showToast("The current password is incorrect.", "error", 3000);
          break;
        case "auth/user-not-found":
          useToastStore
            .getState()
            .showToast(
              "User account not found. Please log in again.",
              "error",
              3000
            );
          break;
        case "auth/too-many-requests":
          useToastStore
            .getState()
            .showToast(
              "Too many failed attempts. Try again later.",
              "error",
              3000
            );
          break;
        case "auth/requires-recent-login":
          useToastStore
            .getState()
            .showToast(
              "Your session has expired. Please log in again.",
              "error",
              3000
            );
          break;
        default:
          useToastStore
            .getState()
            .showToast(
              "Failed to change password. Please try again.",
              "error",
              3000
            );
      }
      console.error("Password change error:", error);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      showLoader();
      try {
        setLoading(true);
        const [privacyResponse, profileData] = await Promise.all([
          getPrivacySettings(), // anonymous toggle state
          getUserProfile(), // name + airlines + homebase
        ]);
        setRespondAnonymously(privacyResponse.isAnonymous);
        setProfile(profileData);
        // The name field is the account name (fullName) — seed it from the
        // profile so it reflects what's actually persisted.
        if (profileData?.fullName) {
          setDisplayName(String(profileData.fullName));
          setTempName(String(profileData.fullName));
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      } finally {
        hideLoader();
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Validate password on change
  useEffect(() => {
    setHasLowercase(/[a-z]/.test(newPassword));
    setHasUppercase(/[A-Z]/.test(newPassword));
    setHasNumber(/[0-9]/.test(newPassword));
    setHasSpecial(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword));
    setHasMinLength(newPassword.length >= 6);
    setPasswordsMatch(newPassword === confirmPassword && newPassword !== "");
  }, [newPassword, confirmPassword]);

  const isPasswordValid = () => {
    return (
      hasLowercase &&
      hasUppercase &&
      hasNumber &&
      hasSpecial &&
      hasMinLength &&
      passwordsMatch &&
      currentPassword.length > 0
    );
  };

  const handleCheckboxChange = async () => {
    try {
      // Toggle the checkbox state
      setRespondAnonymously((prevState) => !prevState);

      // Call the API to update the anonymous status
      await setAnonymousStatus(!respondAnonymously);
    } catch (error) {
      console.error("Error updating anonymous status:", error);
    }
  };

  return (
    <div className="privacy">
      <div className="bg-white p-6 rounded-xl">
        {/* Section Header */}
        <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-100">
          <i className="st-shield text-grease-100 text-lg"></i>
          <p className="text-grease-100 text-base font-semibold">Privacy</p>
        </div>

        {/* Display Name Field */}
        <div className="max-w-sm">
          <label className="block mb-1.5 text-xs font-medium text-grease-100">
            Display Name
          </label>
            <div className="relative">
              <input
                type="text"
                value={String(tempName)}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = e.clipboardData.getData('text');
                  const currentValue = String(tempName);
                  const newValue = (currentValue + pastedText).substring(0, 20);
                  if (newValue.length > 20) {
                    setError("*Name must be 20 characters or less");
                  } else if (!/^[a-zA-Z\s]*$/.test(newValue)) {
                    setError("*Only letters and spaces are allowed");
                  } else {
                    setError("");
                  }
                  setTempName(newValue);
                }}
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  if (target.value.length > 20) {
                    target.value = target.value.substring(0, 20);
                    setError("*Name must be 20 characters or less");
                    setTempName(target.value);
                  }
                }}
                disabled={!isEditing}
                maxLength={20}
                className="block w-full p-3.5 rounded-lg bg-white text-sm text-grease-100 border border-gray-200 focus:outline-none focus:border-light-yellow disabled:text-gray-500 pr-12"
              />
              <div className="absolute inset-y-0 right-4 flex items-center space-x-2">
                {isEditing ? (
                  <>
                    <button
                      onClick={handleCancel}
                      className="text-gray-600 hover:text-gray-600"
                    >
                      <X size={18} />
                    </button>
                    {tempName && tempName !== displayName && !error && (
                      <button
                        onClick={handleNameSave}
                        className="text-green-500 hover:text-green-700"
                      >
                        <Check size={18} />
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    onClick={handleEditClick}
                    className="absolute right-3 text-gray-600 hover:text-gray-600"
                  >
                    <Pencil size={18} />
                  </button>
                )}
              </div>
            </div>
            {error && <p className="mt-1 ml-1 text-xs text-red-500">{error}</p>}
        </div>

        {/* Additional Airlines / Anonymous section */}
        <p className="text-grease-100 text-sm font-semibold mt-8 mb-4">Additional Airlines</p>

        {/* Respond Anonymously Toggle */}
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl max-w-lg">
          <div>
            <p className="text-sm font-medium text-grease-100">
              Respond to requests anonymously
            </p>
            <p className="mt-1 text-xs text-gray-400">
              Your first name will be shown when you respond to a loads request
            </p>
          </div>
          <label className="inline-flex cursor-pointer flex-shrink-0 ml-4">
            <input
              type="checkbox"
              checked={respondAnonymously}
              onChange={handleCheckboxChange}
              className="sr-only peer"
            />
            <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FFBC00]" />
          </label>
        </div>
        {/* Change Password Section */}
        <p className="text-grease-100 text-sm font-semibold mt-8 mb-4">
          Change Password
        </p>
        {passwordError && (
          <p className="text-red-500 text-sm mb-4">{passwordError}</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {/* Current Password Field */}
          <div>
            <label className="block mb-1.5 text-xs font-medium text-grease-100">
              Current Password
            </label>
            <div className="relative">
              <input
                ref={currentPasswordRef}
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={currentPassword}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length > 20) {
                    setCurrentPasswordLengthError("Password must be 20 characters or less");
                    setCurrentPassword(value.substring(0, 20));
                  } else {
                    setCurrentPasswordLengthError("");
                    setCurrentPassword(value);
                  }
                }}
                onKeyDown={(e) => {
                  if (currentPassword.length >= 20 &&
                      e.key !== 'Backspace' &&
                      e.key !== 'Delete' &&
                      e.key !== 'ArrowLeft' &&
                      e.key !== 'ArrowRight' &&
                      e.key !== 'Tab' &&
                      e.key !== 'Home' &&
                      e.key !== 'End' &&
                      !e.ctrlKey &&
                      !e.metaKey &&
                      !e.altKey) {
                    e.preventDefault();
                    setCurrentPasswordLengthError("Password must be 20 characters or less");
                  }
                }}
                minLength={6}
                maxLength={20}
                className={`pw-field ${showCurrentPassword ? "" : "pw-mask"} block w-full p-3.5 pr-12 rounded-lg bg-white text-sm text-grease-100 border border-gray-200 focus:outline-none focus:border-light-yellow`}
              />
              {currentPasswordLengthError && <p className="text-red-500 mt-1 text-xs">{currentPasswordLengthError}</p>}
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute inset-y-0 right-4 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showCurrentPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          {/* New Password Field */}
          <div>
            <label className="block mb-1.5 text-xs font-medium text-grease-100">
              New Password
            </label>
            <div className="relative">
              <input
                ref={newPasswordRef}
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={newPassword}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length > 20) {
                    setNewPasswordLengthError("Password must be 20 characters or less");
                    setNewPassword(value.substring(0, 20));
                  } else {
                    setNewPasswordLengthError("");
                    setNewPassword(value);
                  }
                }}
                onKeyDown={(e) => {
                  if (newPassword.length >= 20 &&
                      e.key !== 'Backspace' &&
                      e.key !== 'Delete' &&
                      e.key !== 'ArrowLeft' &&
                      e.key !== 'ArrowRight' &&
                      e.key !== 'Tab' &&
                      e.key !== 'Home' &&
                      e.key !== 'End' &&
                      !e.ctrlKey &&
                      !e.metaKey &&
                      !e.altKey) {
                    e.preventDefault();
                    setNewPasswordLengthError("Password must be 20 characters or less");
                  }
                }}
                minLength={6}
                maxLength={20}
                className={`pw-field ${showNewPassword ? "" : "pw-mask"} block w-full p-3.5 pr-12 rounded-lg bg-white text-sm text-grease-100 border border-gray-200 focus:outline-none focus:border-light-yellow`}
              />
              {newPasswordLengthError && <p className="text-red-500 mt-1 text-xs">{newPasswordLengthError}</p>}
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute inset-y-0 right-4 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showNewPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          {/* Confirm New Password Field */}
          <div>
            <label className="block mb-1.5 text-xs font-medium text-grease-100">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                ref={confirmPasswordRef}
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={confirmPassword}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length > 20) {
                    setConfirmPasswordLengthError("Password must be 20 characters or less");
                    setConfirmPassword(value.substring(0, 20));
                  } else {
                    setConfirmPasswordLengthError("");
                    setConfirmPassword(value);
                  }
                }}
                onKeyDown={(e) => {
                  if (confirmPassword.length >= 20 &&
                      e.key !== 'Backspace' &&
                      e.key !== 'Delete' &&
                      e.key !== 'ArrowLeft' &&
                      e.key !== 'ArrowRight' &&
                      e.key !== 'Tab' &&
                      e.key !== 'Home' &&
                      e.key !== 'End' &&
                      !e.ctrlKey &&
                      !e.metaKey &&
                      !e.altKey) {
                    e.preventDefault();
                    setConfirmPasswordLengthError("Password must be 20 characters or less");
                  }
                }}
                minLength={6}
                maxLength={20}
                className={`pw-field ${showConfirmPassword ? "" : "pw-mask"} block w-full p-3.5 pr-12 rounded-lg bg-white text-sm text-grease-100 border border-gray-200 focus:outline-none focus:border-light-yellow`}
              />
              {confirmPasswordLengthError && <p className="text-red-500 mt-1 text-xs">{confirmPasswordLengthError}</p>}
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-4 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Password Requirements */}
        <div className="flex flex-col gap-2 mt-5">
          <div className="flex gap-2 items-center">
            <div className={`flex items-center justify-center w-4 h-4 text-xs font-medium rounded-full ${hasLowercase ? "bg-[#FFD700]" : "bg-gray-200"}`}>
              {hasLowercase && <img src="images/check.svg" alt="" />}
            </div>
            <p className="text-gray-500 text-xs">One lowercase character</p>
          </div>
          <div className="flex gap-2 items-center">
            <div className={`flex items-center justify-center w-4 h-4 text-xs font-medium rounded-full ${hasUppercase ? "bg-[#FFD700]" : "bg-gray-200"}`}>
              {hasUppercase && <img src="images/check.svg" alt="" />}
            </div>
            <p className="text-gray-500 text-xs">One uppercase character</p>
          </div>
          <div className="flex gap-2 items-center">
            <div className={`flex items-center justify-center w-4 h-4 text-xs font-medium rounded-full ${hasNumber ? "bg-[#FFD700]" : "bg-gray-200"}`}>
              {hasNumber && <img src="images/check.svg" alt="" />}
            </div>
            <p className="text-gray-500 text-xs">One number</p>
          </div>
          <div className="flex gap-2 items-center">
            <div className={`flex items-center justify-center w-4 h-4 text-xs font-medium rounded-full ${hasSpecial ? "bg-[#FFD700]" : "bg-gray-200"}`}>
              {hasSpecial && <img src="images/check.svg" alt="" />}
            </div>
            <p className="text-gray-500 text-xs">One special character</p>
          </div>
          <div className="flex gap-2 items-center">
            <div className={`flex items-center justify-center w-4 h-4 text-xs font-medium rounded-full ${hasMinLength ? "bg-[#FFD700]" : "bg-gray-200"}`}>
              {hasMinLength && <img src="images/check.svg" alt="" />}
            </div>
            <p className="text-gray-500 text-xs">8 characters minimum</p>
          </div>
          {confirmPassword && (
            <div className="flex gap-2 items-center">
              <div className={`flex items-center justify-center w-4 h-4 text-xs font-medium rounded-full ${passwordsMatch ? "bg-[#FFD700]" : "bg-gray-200"}`}>
                {passwordsMatch && <img src="images/check.svg" alt="" />}
              </div>
              <p className="text-gray-500 text-xs">Passwords match</p>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex justify-end items-center gap-4 mt-8">
          <button
            onClick={handleCancel}
            className="px-6 py-2.5 rounded-lg text-grease-100 text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handlePasswordSave}
            className={`px-6 py-2.5 rounded-lg text-white text-sm font-medium transition-colors ${
              isPasswordValid() ? "bg-[#FFBC00] hover:opacity-90" : "bg-[#FFBC00] opacity-50 cursor-not-allowed"
            }`}
            disabled={!isPasswordValid()}
          >
            Save
          </button>
        </div>
      </div>
      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)} // 👈 Close modal
      />
    </div>
  );
};

export default PrivacySettings;
